/**
 * Fall detection wearer-phone field (AUD-613).
 *
 * Toggle reveals properties[Wearer Phone]. On submit, normalizes to E.164 using
 * the same rules as AudienAutomatedReturnFlow/entitlements/normalize.js so
 * Recharge → Fivetran → ENTITLEMENTS → Upstash all key on the same phone.
 */
(function () {
  function toE164(input) {
    if (input === null || input === undefined) return null;
    const digits = String(input).replace(/\D/g, "");
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
    return null;
  }

  class WlsnFallDetectionWearerPhone extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      this.toggle = this.querySelector("[data-wearer-toggle]");
      this.fields = this.querySelector("[data-wearer-fields]");
      this.input = this.querySelector("[data-wearer-phone]");
      this.error = this.querySelector("[data-wearer-error]");
      this.form = this.closest("form");

      if (!this.toggle || !this.fields || !this.input) return;

      this.toggle.addEventListener("change", () => this.syncVisibility());
      this.input.addEventListener("input", () => this.clearError());
      this.input.addEventListener("blur", () => {
        if (!this.toggle.checked) return;
        const normalized = toE164(this.input.value);
        if (normalized) this.input.value = normalized;
      });

      if (this.form) {
        this.form.addEventListener(
          "submit",
          (event) => {
            if (!this.validateBeforeSubmit()) {
              event.preventDefault();
              event.stopImmediatePropagation();
            }
          },
          true,
        );

        // Dynamic checkout (Apple Pay / Google Pay / Shop Pay) can bypass
        // form submit — block those clicks until Wearer Phone validates.
        this.form.addEventListener(
          "click",
          (event) => {
            const paymentClick = event.target.closest(
              ".shopify-payment-button__button, .shopify-payment-button button, shopify-accelerated-checkout",
            );
            if (!paymentClick) return;
            if (!this.validateBeforeSubmit()) {
              event.preventDefault();
              event.stopImmediatePropagation();
            }
          },
          true,
        );
      }

      this.syncVisibility();
    }

    syncVisibility() {
      const buyingForSomeoneElse = !!this.toggle.checked;
      this.fields.hidden = !buyingForSomeoneElse;
      this.input.disabled = !buyingForSomeoneElse;
      this.input.required = buyingForSomeoneElse;
      if (!buyingForSomeoneElse) {
        this.input.value = "";
        this.clearError();
      }
    }

    clearError() {
      if (!this.error) return;
      this.error.hidden = true;
      this.error.textContent = "";
      this.input.removeAttribute("aria-invalid");
    }

    showError(message) {
      if (!this.error) return;
      this.error.hidden = false;
      this.error.textContent = message;
      this.input.setAttribute("aria-invalid", "true");
      this.input.focus();
    }

    validateBeforeSubmit() {
      return this.validate();
    }

    /**
     * @returns {boolean}
     */
    validate() {
      if (!this.toggle || !this.input) return true;

      if (!this.toggle.checked) {
        // Buying for self — omit property; Recharge/Snowflake fall back to customer phone.
        this.input.disabled = true;
        this.input.value = "";
        return true;
      }

      const normalized = toE164(this.input.value);
      if (!normalized) {
        this.showError(
          this.dataset.invalidMessage ||
            "Enter a valid US phone number (10 digits).",
        );
        return false;
      }

      this.input.disabled = false;
      this.input.value = normalized;
      this.clearError();
      return true;
    }

    /**
     * Line-item properties for /cart/add.js. Empty when buying for self.
     * @returns {Record<string, string>|null} null when invalid
     */
    getCartProperties() {
      if (!this.validate()) return null;
      if (!this.toggle?.checked) return {};
      return { "Wearer Phone": this.input.value };
    }
  }

  if (!customElements.get("wlsn-fall-detection-wearer-phone")) {
    customElements.define(
      "wlsn-fall-detection-wearer-phone",
      WlsnFallDetectionWearerPhone,
    );
  }

  window.toE164 = toE164;
})();
